import { clone } from '@jkhy/frontend-utils/functions';
import {
    GuidKeyCollectionDto,
    NotificationConfigurationFeatureModelDto,
    NotificationConfigurationGroupModelDto,
    NotificationConfigurationModelDto,
    ThresholdDto,
} from '@treasury/api/channel';
import { DomainEntity } from '../../../../shared';

export class NotificationThreshold extends DomainEntity<ThresholdDto> {
    constructor(dto = clone({} as ThresholdDto)) {
        super(dto);
    }

    public createDefault(): this {
        throw new Error('Method not implemented.');
    }

    public get id() {
        return this.dto.id;
    }

    public get underAmount() {
        return this.dto.underAmount;
    }

    public get overAmount() {
        return this.dto.overAmount;
    }

    public isDeleted() {
        return this.dto.isDeleted;
    }

    public get accountIds() {
        return this.dto.accountIds as GuidKeyCollectionDto;
    }
}

export class NotificationConfigurationFeature extends DomainEntity<NotificationConfigurationFeatureModelDto> {
    constructor(
        dto = clone({
            id: 0,
            updatedDate: '',
            createdDate: '',
            name: '',
            isDesktopRequired: false,
            isActive: false,
            isChanged: false,
            isEmailAllowed: false,
            isEmailActive: false,
            isEmailRequired: false,
            isDesktopAllowed: false,
            isDesktopActive: false,
            isTextMessageActive: false,
            isTextMessageAllowed: false,
            isTextMessageRequired: false,
            notificationTypeId: 0,
            sequence: 0,
            sortOrder: 0,
            threshold: {
                id: '',
                underAmount: 0,
                overAmount: 0,
                isDeleted: false,
                accountIds: [],
            },
        } as NotificationConfigurationFeatureModelDto)
    ) {
        super(dto);
    }

    public createDefault(): this {
        throw new Error('Method not implemented.');
    }

    public get name() {
        return this.dto.name;
    }

    public set name(value: string) {
        this.dto.name = value;
    }

    public get isDesktopRequired() {
        return this.dto.isDesktopRequired;
    }

    public set isDesktopRequired(value: boolean) {
        this.dto.isDesktopRequired = value;
    }

    public get isActive() {
        return this.dto.isActive;
    }

    public set isActive(value: boolean) {
        this.dto.isActive = value;
    }

    public get isChanged() {
        return this.dto.isChanged;
    }

    public set isChanged(value: boolean) {
        this.dto.isChanged = value;
    }

    public get isEmailAllowed() {
        return this.dto.isEmailAllowed;
    }

    public set isEmailAllowed(value: boolean) {
        this.dto.isEmailAllowed = value;
    }

    public get isEmailActive() {
        return this.dto.isEmailActive;
    }

    public set isEmailActive(value: boolean) {
        this.dto.isEmailActive = value;
    }

    public get isEmailRequired() {
        return this.dto.isEmailRequired;
    }

    public get isDesktopAllowed() {
        return this.dto.isDesktopAllowed;
    }

    public set isDesktopAllowed(value: boolean) {
        this.dto.isDesktopAllowed = value;
    }

    public get isDesktopActive() {
        return this.dto.isDesktopActive;
    }

    public set isDesktopActive(value: boolean) {
        this.dto.isDesktopActive = value;
    }

    public get isTextMessageActive() {
        return this.dto.isTextMessageActive;
    }

    public set isTextMessageActive(value: boolean) {
        this.dto.isTextMessageActive = value;
    }

    public get isTextMessageAllowed() {
        return this.dto.isTextMessageAllowed;
    }

    public set isTextMessageAllowed(value: boolean) {
        this.dto.isTextMessageAllowed = value;
    }

    public get isTextMessageRequired() {
        return this.dto.isTextMessageRequired;
    }

    public get notificationTypeId() {
        return this.dto.notificationTypeId;
    }

    public get sequence() {
        return this.dto.sequence;
    }

    public set sequence(value: number) {
        this.dto.sequence = value;
    }

    public get sortOrder() {
        return this.dto.sortOrder;
    }

    public get threshold() {
        return new NotificationThreshold(this.dto.threshold);
    }
}

export class NotificationConfigurationGroup extends DomainEntity<NotificationConfigurationGroupModelDto> {
    constructor(dto = clone({} as NotificationConfigurationGroupModelDto)) {
        super(dto);
    }

    private _filteredFeatures: NotificationConfigurationFeature[] =
        this.dto.features
            ?.map(f => new NotificationConfigurationFeature(f))
            .sort((a, b) => a.sequence - b.sequence) || [];

    private _isOpen = false;

    public createDefault(): this {
        throw new Error('Method not implemented.');
    }

    public get name() {
        return this.dto.name;
    }

    public get features() {
        const sortedFeatures =
            this.dto.features
                ?.map(f => new NotificationConfigurationFeature(f))
                .sort((a, b) => a.sequence - b.sequence) || [];
        this.filteredFeatures = sortedFeatures;
        return sortedFeatures;
    }

    public set features(value: NotificationConfigurationFeature[]) {
        this.dto.features = value.map(f => f.toDto());
        const sortedFeatures = value.sort((a, b) => a.sequence - b.sequence);
        this.filteredFeatures = sortedFeatures;
    }

    public get filteredFeatures() {
        return this._filteredFeatures;
    }

    public set filteredFeatures(value: NotificationConfigurationFeature[]) {
        this._filteredFeatures = value;
    }

    public get isOpen() {
        return this._isOpen;
    }

    public set isOpen(value: boolean) {
        this._isOpen = value;
    }
}

export class NotificationConfiguration extends DomainEntity<NotificationConfigurationModelDto> {
    constructor(dto = clone({} as NotificationConfigurationModelDto)) {
        super(dto);
    }

    private _groups: NotificationConfigurationGroup[] = !this.dto.groups?.length
        ? []
        : this.dto.groups
              .map(g => new NotificationConfigurationGroup(g))
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((group, index) => {
                  group.isOpen = index === 0; // Open the first group by default
                  return group;
              });

    public createDefault(): this {
        throw new Error('Method not implemented.');
    }

    public get email() {
        return this.dto.email;
    }

    public set email(value: string) {
        this.dto.email = value;
    }

    public get isDuplicateEmail() {
        return this.dto.isDuplicateEmail;
    }

    public get phoneNumber() {
        return this.dto.phoneNumber;
    }

    public set phoneNumber(value: string | undefined) {
        this.dto.phoneNumber = value;
    }

    public get isFirstTimeLoad() {
        return this.dto.isFirstTimeLoad;
    }

    public get groups() {
        return this._groups;
    }

    public set groups(value: NotificationConfigurationGroup[]) {
        this._groups = value;
        this.dto.groups = value.map(group => group.toDto());
    }

    public getFeatureByName(name: string): NotificationConfigurationFeature | undefined {
        return this.groups.flatMap(group => group.features).find(feature => feature.name === name);
    }
}
